import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { type CommentsDrawerState, commentsDrawerAtom } from '@/atoms/comments';

export interface DrawerState {
    isOpen: boolean;
    focusedCommentId: number | null;
    commentableId: number | null;
    commentableModel: string | null;
    position?: string;
    showTopInput: boolean;
    filterName?: string;
    textAtomContent: string;
    selectionIndex: number;
    group?: string;
    isCommentBlockFocusClick: boolean;
}

export interface DrawerActions {
    open: (params: {
        commentableModel: string;
        commentableId: number;
        position: string;
        filter?: string;
        filterName?: string;
        showTopInput?: boolean;
        focusComment?: number;
        group?: string;
        isCommentBlockFocusClick?: boolean;
    }) => void;
    openWithQuote: (params: {
        commentableModel: string;
        commentableId: number;
        position: string;
        text: string;
        textAtomContent: string;
        selectionIndex: number;
        group: string;
        filter?: string;
        showTopInput?: boolean;
    }) => void;
    show: () => void;
    close: () => void;
    focus: (commentId: number, commentableId: number, isCommentBlockFocusClick: boolean) => void;
    resetFocus: () => void;
    hideTopInput: () => void;
    setGroup: (group: string) => void;
}

export function useCommentsDrawer(): [DrawerState, DrawerActions] {
    const [raw, setRaw] = useAtom(commentsDrawerAtom);

    const state: DrawerState = useMemo(
        () => ({
            isOpen: raw.show,
            focusedCommentId: raw.focusComment,
            commentableId: raw.commentableId,
            commentableModel: raw.commentableModel,
            position: raw.position,
            showTopInput: raw.showTopInput,
            filterName: raw.filterName,
            textAtomContent: raw.textAtomContent,
            selectionIndex: raw.selectionIndex,
            group: raw.group,
            isCommentBlockFocusClick: raw.isCommentBlockFocusClick,
        }),
        [raw],
    );

    const actions: DrawerActions = useMemo(
        () => ({
            open: params =>
                setRaw((prev: CommentsDrawerState) => ({
                    ...prev,
                    ...params,
                    showTopInput: params.showTopInput ?? true,
                    show: true,
                })),
            openWithQuote: params =>
                setRaw((prev: CommentsDrawerState) => ({
                    ...prev,
                    ...params,
                    showTopInput: params.showTopInput ?? true,
                    show: true,
                })),
            show: () => setRaw(prev => ({ ...prev, show: true })),
            close: () => setRaw(prev => ({ ...prev, show: false, focusComment: null })),
            focus: (commentId, commentableId, isCommentBlockFocusClick) =>
                setRaw(prev => ({
                    ...prev,
                    show: true,
                    focusComment: commentId,
                    commentableId,
                    isCommentBlockFocusClick,
                })),
            resetFocus: () => setRaw(prev => ({ ...prev, focusComment: null })),
            hideTopInput: () => setRaw(prev => ({ ...prev, showTopInput: false })),
            setGroup: (group: string) => setRaw(prev => ({ ...prev, group })),
        }),
        [setRaw],
    );

    return [state, actions];
}
